import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraDataTable } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraPageHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { rfiSectionClient, useRfiPeople } from '../rfiSectionHooks';
import { useRfiRecord } from './RfiRecordContext';
import { useRfiAnswerSections } from './useRfiAnswerSections';
/** Reviews (WRT-07 page 2): answers waiting for review, changes requested or edited after final. */ export function RfiReviewsPage() {
    const { id = '' } = useParams();
    const record = useRfiRecord();
    const rfiId = record?.pangaeaId ?? '';
    const { sections } = useRfiAnswerSections(rfiId);
    const { data: people = [] } = useRfiPeople();
    const events = useQuery({
        queryKey: [
            'rfi-section-events',
            rfiId
        ],
        enabled: Boolean(rfiId),
        queryFn: async ()=>(await rfiSectionClient.listRfiSectionEvents({
                rfiResponseProjectId: rfiId
            })).events
    });
    const rows = useMemo(()=>{
        const names = new Map(people.map((p)=>[
                p.userId,
                p.name || p.email
            ]));
        const lastReview = new Map();
        for (const e of events.data ?? []){
            if (e.kind === 'review_state' && e.createdAt) lastReview.set(e.sectionId, e.createdAt.toDate());
        }
        const out = [];
        sections.forEach((s, i)=>{
            let state = null;
            if (s.questionStatus === 'final_edited') state = {
                label: 'Edited after final',
                variant: 'warning'
            };
            else if (s.reviewState === 'pending') state = {
                label: 'In review',
                variant: 'info'
            };
            else if (s.reviewState === 'changes_requested') state = {
                label: 'Changes requested',
                variant: 'warning'
            };
            if (!state) return;
            out.push({
                n: i + 1,
                sectionId: s.id,
                title: s.title,
                reviewer: s.reviewerUserId ? names.get(s.reviewerUserId) ?? s.reviewerUserId : '',
                requestedAt: lastReview.get(s.id),
                state
            });
        });
        return out;
    }, [
        sections,
        people,
        events.data
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-4)] p-[var(--space-6)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                title: "Reviews",
                titleSize: "ui"
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                columns: [
                    {
                        id: 'q',
                        header: 'Question',
                        cell: (r)=>/*#__PURE__*/ _jsxs(SpecraLink, {
                                href: `/rfi-response/${id}/questions/${r.sectionId}`,
                                renderLink: renderRouterLink,
                                children: [
                                    "#",
                                    r.n,
                                    " ",
                                    r.title
                                ]
                            })
                    },
                    {
                        id: 'reviewer',
                        header: 'Reviewer',
                        cell: (r)=>r.reviewer || '—'
                    },
                    {
                        id: 'requested',
                        header: 'Requested',
                        cell: (r)=>r.requestedAt ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: formatDistanceToNow(r.requestedAt, {
                                    addSuffix: true
                                })
                            }) : '—'
                    },
                    // TODO(WRT-12): open comment counts arrive with the comments rail.
                    {
                        id: 'comments',
                        header: 'Open comments',
                        cell: ()=>'—'
                    },
                    {
                        id: 'state',
                        header: 'State',
                        cell: (r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: r.state.variant,
                                children: r.state.label
                            })
                    }
                ],
                emptyTitle: "Nothing waiting for review.",
                getRowId: (r)=>r.sectionId,
                getRowLabel: (r)=>`Question ${r.n}`,
                rows: rows,
                testId: Testids.rfiWorkspace.reviewsTable
            })
        ]
    });
}
